import { CheckCircleFilled } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Button } from 'antd';
import { isBoolean } from 'lodash-es';
import React, { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { StandaloneAuthResult } from 'services/api/admin';
import { ShopeeAuthWay } from './ShopeeShopAuthWay';

const ShopTableCell = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 64px;
  text-align: left;
  padding: 0 16px;
`;
const ShopAuthData = styled.span`
  margin-right: 42px;
  min-width: 120px;
`;
const dot = css`
  display: inline-block;
  position: relative;
  padding-left: 16px;

  &:before {
    content: '';
    position: absolute;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  &.wait:before {
    background-color: var(--ant-warning-color);
  }
  &.success:before {
    background-color: var(--ant-success-color);
  }
  &.failed:before {
    background-color: var(--ant-error-color);
  }
`;
const SuccessIcon = styled(CheckCircleFilled)`
  color: var(--ant-success-color);
  margin-right: 8px;
`;

export interface ShopeeSyncShopTypeProps {
  chatPartnerType?: boolean;
  erpPartnerType?: boolean;
  authWay?: ShopeeAuthWay;
  result?: boolean;
  errorMsg?: string;
  onAuthResult?: (type: IM_API.Admin.PartnerType, value: StandaloneAuthResult) => void;
  setSyncLoading: (value: boolean) => void;
  onOpen: () => void;
  onCopy: (noPrefix?: boolean) => void;
  loading: boolean;
}

const ShopeeSyncShopType = (props: ShopeeSyncShopTypeProps) => {
  const { authWay, chatPartnerType, erpPartnerType, result, errorMsg, onOpen, onCopy, loading } =
    props;
  const { t } = useTranslate();

  const isResultBoolean = useMemo(() => isBoolean(result), [result]);

  const btn = useMemo(() => {
    if (isResultBoolean && result) {
      return (
        <div>
          <SuccessIcon />
          {t('authShop.已完成')}
        </div>
      );
    }
    if (authWay === ShopeeAuthWay.Official) {
      return (
        <>
          <Button type="primary" size="small" onClick={onOpen}>
            {t('authShop.点击此处前往卖家中心授权')}
          </Button>
          <Button className="ml-[12px]" size="small" type="primary" onClick={() => onCopy(true)}>
            {t('authShop.复制链接')}
          </Button>
        </>
      );
    }
    if (authWay === ShopeeAuthWay.External) {
      return (
        <Button type="primary" onClick={() => onCopy()}>
          {t('authShop.复制链接')}
        </Button>
      );
    }
    return null;
  }, [authWay, isResultBoolean, onCopy, onOpen, result, t]);

  const status = useMemo(() => {
    if (loading) {
      return (
        <Button type="text" loading={loading}>
          {t('authShop.添加中')}
        </Button>
      );
    }
    if (isResultBoolean) {
      if (result) {
        return <div className={`${dot} success`}>{t('authShop.授权成功')}</div>;
      }
      return (
        <div className={`${dot} failed`}>
          {t('authShop.授权失败')} {errorMsg}
        </div>
      );
    }
    return <div className={`${dot} wait`}>{t('authShop.等待授权')}</div>;
  }, [errorMsg, isResultBoolean, loading, result, t]);

  if (chatPartnerType) {
    return (
      <>
        <ShopTableCell>
          <ShopAuthData>{t('authShop.买家消息数据授权')}</ShopAuthData>
          {btn}
        </ShopTableCell>
        <ShopTableCell className="min-w-[240px]">{status}</ShopTableCell>
      </>
    );
  }

  if (erpPartnerType) {
    return (
      <>
        <ShopTableCell>
          <ShopAuthData>{t('authShop.商品订单数据授权')}</ShopAuthData>
          {btn}
        </ShopTableCell>
        <ShopTableCell className="min-w-[240px]">{status}</ShopTableCell>
      </>
    );
  }

  return null;
};

export default ShopeeSyncShopType;
